import { FC } from 'react';
import {
  ActionsMenu,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRouteParams } from 'utils/useIdParam';
import { useSearchParams } from 'utils/navigation';
import {
  adminTransmissionActsFiltersSchema,
  useAdminTransmissionActs,
  useAdminDownloadTransmissionActPdfOnServer,
  useExportToXls,
  useRegenerateTransmissionActs,
} from 'store/admin-transmission-acts';
import { formatCurrency } from 'utils/format';
import { APP, ROUTES } from 'config';
import { useAdminClient } from 'store/admin-clients';
import {
  AdminCompaniesFilter,
  DateRangeFilter,
  FilterButton,
  Filters,
} from 'components/filters';
import moment from 'moment';
import { usePredefinedToasts } from 'utils/Toast';
import { useModalContext } from 'store/modal';
import { EditTransmissionActModal, SendTransmissionActModal } from './atoms';

export const AdminTransmissionActsList: FC = () => {
  const { t, i18n } = useTranslation();
  const { id: clientId } = useRouteParams(
    ROUTES.admin.clients.details.transmissionActs.list,
  );
  const { openModal } = useModalContext();
  const predefinedToasts = usePredefinedToasts();

  const { searchParams: filters, navigate } = useSearchParams(
    adminTransmissionActsFiltersSchema,
  );

  const { data: transmissionActs, isLoading } = useAdminTransmissionActs({
    ...filters,
    parentCompany: clientId,
  });
  const { data: client } = useAdminClient(clientId);

  const { mutate: regenerateTransmissionActs, isLoading: isRegenerating } =
    useRegenerateTransmissionActs();
  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();
  const { mutate: downloadPdf, isLoading: isDownloading } =
    useAdminDownloadTransmissionActPdfOnServer();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <ListTotal total={transmissionActs?.total} />

            <Stack direction="row" alignItems="center" gap={1}>
              <Filters
                value={filters}
                initialValues={adminTransmissionActsFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              >
                {({ value, initialValues, setFilter }) => (
                  <>
                    <FilterButton
                      label={t('common.date')}
                      value={value.date}
                      initialValue={initialValues.date}
                      onChange={(v) => setFilter('date', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <DateRangeFilter
                          value={value}
                          onApply={applyFilter}
                          onClear={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('employees.company')}
                      value={value.companies}
                      initialValue={initialValues.companies}
                      onChange={(v) => setFilter('companies', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <AdminCompaniesFilter
                          clientId={clientId}
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>
                  </>
                )}
              </Filters>
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={transmissionActs?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'id',
            header: t('integrations.hiBobServiceUserId'),
            cell: ({ row: { original: transmissionAct } }) => {
              return transmissionAct.sequence || transmissionAct.id;
            },
          },
          {
            id: 'date',
            header: t('common.date'),
            cell: ({ row: { original: transmissionAct } }) => {
              return moment(transmissionAct.from).format('MM YYYY');
            },
            meta: { sort: true },
          },
          {
            id: 'company',
            header: t('employees.company'),
            cell: ({ row: { original: transmissionAct } }) => {
              return transmissionAct.company?.name ?? null;
            },
            meta: { sort: true },
          },
          {
            id: 'openingBalance',
            header: t('transmissionActs.openingBalance.label'),
            cell: ({ row: { original: transmissionAct } }) => {
              return (
                <Typography
                  color={
                    transmissionAct.openingBalance < 0 ? 'error' : 'textPrimary'
                  }
                >
                  {formatCurrency(
                    i18n.language,
                    transmissionAct.openingBalance,
                    client?.defaultCurrency,
                  )}
                </Typography>
              );
            },
            meta: { align: 'right' },
          },
          {
            id: 'closingBalance',
            header: t('transmissionActs.closingBalance.label'),
            cell: ({ row: { original: transmissionAct } }) => {
              return (
                <Typography
                  color={
                    transmissionAct.closingBalance < 0 ? 'error' : 'textPrimary'
                  }
                >
                  {formatCurrency(
                    i18n.language,
                    transmissionAct.closingBalance,
                    client?.defaultCurrency,
                  )}
                </Typography>
              );
            },
            meta: { align: 'right' },
          },
          {
            id: 'actions',
            cell: ({ row: { original: transmissionAct } }) => {
              return (
                <ActionsMenu
                  type="compact"
                  items={[
                    {
                      label: t('common.edit'),
                      onClick: () => {
                        if (client) {
                          openModal({
                            component: EditTransmissionActModal,
                            props: {
                              transmissionActId: transmissionAct.id,
                              initialValues: {
                                openingBalance: transmissionAct.openingBalance,
                                closingBalance: transmissionAct.closingBalance,
                                currency: client.defaultCurrency,
                              },
                            },
                          });
                        }
                      },
                    },
                    {
                      label: t('transmissionActs.actions.regenerate'),
                      onClick: () => {
                        regenerateTransmissionActs(
                          {
                            clientId,
                            data: {
                              month: moment(transmissionAct.from).month() + 1,
                              year: moment(transmissionAct.from).year(),
                              companyId: transmissionAct.company?.id,
                            },
                          },
                          {
                            onSuccess: () => {
                              predefinedToasts.success.updated();
                            },
                          },
                        );
                      },
                      disabled: isRegenerating,
                    },
                    {
                      label: t('dashboard.download_xls'),
                      onClick: () => {
                        exportToXls({ transmissionActId: transmissionAct.id });
                      },
                      disabled: isExportingToXls,
                    },
                    {
                      label: t('common.downloadPDF'),
                      onClick: () => {
                        downloadPdf({
                          transmissionActId: transmissionAct.id,
                        });
                      },
                      disabled: isDownloading,
                    },
                    {
                      label: t('transmissionActs.actions.send_pdf'),
                      onClick: () => {
                        if (!client) return;

                        openModal({
                          component: SendTransmissionActModal,
                          props: {
                            transmissionActId: transmissionAct.id,
                            transmissionActSequence: transmissionAct.sequence,
                            clientId,
                            payload: transmissionAct.company?.id
                              ? { companyId: transmissionAct.company.id }
                              : {
                                  accountantEmails:
                                    client.settings.accountantEmails.map(
                                      (email) => ({ email }),
                                    ),
                                  accountantLanguage:
                                    client.settings.accountantLanguage,
                                },
                          },
                          size: 'xl',
                        });
                      },
                    },
                  ]}
                />
              );
            },
          },
        ]}
        pagination={
          transmissionActs && {
            count: transmissionActs.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
      />
    </TableWrapper>
  );
};
